import { NavLink, Outlet } from "react-router";

import { Box, List, ListItemButton, ListItemText, Stack, Tab, Tabs, Typography } from "@mui/material";

import { MAIN_CONTENT_PADDING_X } from "@/common/components/layout/Layout.constants";
import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";
import withAuthorization from "@/common/hocs/with-authorization/withAuthorization";
import { useTranslation } from "@/translations";

import { systemSettingsNav } from "./SystemSettings.config";
import { useActiveSystemSettingsNav } from "./hooks/useActiveSystemSettingsNav";

function SystemSettingsInner() {
  const { t } = useTranslation();
  const activeGroup = useActiveSystemSettingsNav();

  const activeGroupNav = systemSettingsNav.find(({ group }) => group === activeGroup) ?? systemSettingsNav[0];

  return (
    // No overflow:hidden here — the children manage their own scroll — so the menu's negative-margin
    // breakout (below) is not clipped and can reach the main app sidebar.
    <Stack spacing={2} flex={1} minHeight={0}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        gap={2}
        borderBottom={1}
        borderColor="divider"
      >
        <Typography variant="h1" sx={{ mb: 1 }}>
          {t("settings.pageTitle")}
        </Typography>
        <Tabs
          value={activeGroup}
          aria-label={t("settings.pageTitle")}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          sx={{
            "& .MuiTab-root": { textTransform: "none" },
            "& .Mui-selected": { fontWeight: 700 },
          }}
        >
          {systemSettingsNav.map(({ group, label, entries }) => (
            <Tab key={group} value={group} label={t(label)} component={NavLink} to={entries[0].to} />
          ))}
        </Tabs>
      </Box>

      {/* Cancels the layout <main> horizontal padding so the menu runs flush to the main app sidebar */}
      <Box display="flex" flex={1} minHeight={0} overflow="hidden" sx={{ ml: -MAIN_CONTENT_PADDING_X }}>
        <List
          component="nav"
          aria-label={t(activeGroupNav.label)}
          disablePadding
          sx={{ width: 280, flexShrink: 0, overflow: "auto" }}
        >
          {activeGroupNav.entries.map((entry) => (
            <ListItemButton
              key={entry.to}
              component={NavLink}
              to={entry.to}
              sx={{
                py: 2,
                pl: 3,
                borderBottom: "1px solid",
                borderColor: "divider",
                "& .MuiListItemText-primary": { fontSize: "0.875rem" },
                "&.active": {
                  bgcolor: (theme) => theme.palette.fsw["blue-5"],
                  "& .MuiListItemText-primary": { fontWeight: 700 },
                },
              }}
            >
              <ListItemText primary={t(entry.label)} />
            </ListItemButton>
          ))}
        </List>

        <Stack flex={1} minWidth={0} overflow="hidden" p={3} bgcolor={(theme) => theme.palette.fsw["blue-5"]}>
          <Stack flex={1} minHeight={0} overflow="hidden" p={3} borderRadius={2} bgcolor="background.paper">
            <Outlet />
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}

export const SystemSettings = withAuthorization(SystemSettingsInner, UserPermission.Supervisor);
